import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { SignupCard } from "@/components/nobo/SignupCard";
import { SheetFix } from "@/components/nobo/SheetFix";
import { InvitePanel } from "@/components/nobo/InvitePanel";
import { REGULAR_AT } from "@/components/nobo/SignupCard";

const TABS = [["waitlisted", "waitlisted"], ["invited", "invite drafted"], ["confirmed", "confirmed"], ["no_show", "no-show"], ["all", "all"]];
const statusOf = (r) => r.status || "waitlisted";
const runs = (r) => r.attended_count || 0;

export const SignupList = () => {
  const [rows, setRows] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("waitlisted");
  const [picked, setPicked] = useState(new Set());
  const [inviting, setInviting] = useState(null);

  const load = useCallback(() => api.get("/admin/signups").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => (rows || [])
    .filter((r) => tab === "all" || statusOf(r) === tab)
    .sort((a, b) => runs(b) - runs(a)), [rows, tab]);

  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const openPanel = (kind) => setInviting({ kind, runners: rows.filter((r) => picked.has(r.id)) });
  const closeInvites = () => { setInviting(null); setPicked(new Set()); load(); };
  const undo = async (id) => { await api.delete(`/admin/signups/${id}/invite`); load(); };
  const setStatus = async (id, status) => { await api.put(`/admin/signups/${id}/status`, { status }); load(); };
  const attended = async (id) => { await api.post(`/admin/signups/${id}/attended`); load(); };

  const resend = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/admin/resend");
      setMsg(data.remaining ? `Sent ${data.sent}. The sheet still isn't accepting rows, so ${data.remaining} are waiting.` : `All caught up. Sent ${data.sent} to the sheet.`);
      await load();
    } catch {
      setMsg("Couldn't reach the server. Try again.");
    }
    setBusy(false);
  };

  if (!rows) return <p className="lede" data-testid="signups-loading">loading...</p>;
  const missing = rows.filter((r) => r.sheet_status !== "sent").length;
  const regulars = rows.filter((r) => runs(r) >= REGULAR_AT).length;
  const allNoShow = picked.size > 0 && rows.filter((r) => picked.has(r.id)).every((r) => r.status === "no_show");

  return (
    <section data-testid="signup-list">
      <SheetFix onConnected={load} />
      <div className="org-bar">
        <p className="org-count" data-testid="signup-count"><b>{rows.length}</b> on the waitlist{regulars > 0 && <> · <b data-testid="regulars-count">{regulars}</b> regular{regulars > 1 ? "s" : ""}</>}</p>
        <div className="org-actions">
          <a className="hero-cta" href={`${API}/admin/signups.csv`} data-testid="download-csv-button">download spreadsheet</a>
          {missing > 0 && (
            <button className="pill" onClick={resend} disabled={busy} data-testid="resend-sheet-button">
              {busy ? "sending..." : `send ${missing} missing to Google Sheet`}
            </button>
          )}
        </div>
      </div>
      {msg && <p className="org-msg" data-testid="resend-message">{msg}</p>}
      <div className="org-tabs" role="tablist">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "on" : ""} onClick={() => setTab(k)} data-testid={`tab-${k}`}>
            {label} ({k === "all" ? rows.length : rows.filter((r) => statusOf(r) === k).length})
          </button>
        ))}
      </div>
      {shown.length === 0 && <p className="lede" data-testid="signups-empty">Nobody here yet.</p>}
      <ul className="org-list">
        {shown.map((r) => <SignupCard key={r.id} s={r} picked={picked.has(r.id)} onToggle={() => toggle(r.id)} onUndo={() => undo(r.id)} onStatus={(st) => setStatus(r.id, st)} onAttended={() => attended(r.id)} />)}
      </ul>
      {picked.size > 0 && (
        <div className="org-actionbar" data-testid="invite-action-bar">
          <span>{picked.size} picked</span>
          <button type="button" className="pill dark" onClick={() => setPicked(new Set())} data-testid="clear-picked-button">clear</button>
          {allNoShow && <button type="button" className="pill nudge" onClick={() => openPanel("nudge")} data-testid="draft-nudges-button">missed-you notes</button>}
          <button type="button" className="hero-cta" onClick={() => openPanel("invite")} data-testid="draft-invites-button">draft invites</button>
        </div>
      )}
      {inviting && <InvitePanel runners={inviting.runners} kind={inviting.kind} onClose={closeInvites} />}
    </section>
  );
};
